import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Plus, 
  RefreshCw, 
  Trash2, 
  Link, 
  Copy, 
  CheckCircle2, 
  XCircle, 
  Clock,
  ExternalLink,
  Calendar
} from 'lucide-react';
import { 
  getICalFeeds, 
  createICalFeed, 
  deleteICalFeed, 
  syncICalFeed, 
  syncAllFeeds,
  getPropertyICalUrl
} from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const PLATFORM_OPTIONS = [
  { value: 'airbnb', label: 'Airbnb', color: 'bg-red-500' },
  { value: 'vrbo', label: 'VRBO / HomeAway', color: 'bg-blue-600' },
  { value: 'evolve', label: 'Evolve', color: 'bg-emerald-500' },
  { value: 'booking.com', label: 'Booking.com', color: 'bg-blue-800' },
  { value: 'google', label: 'Google Calendar', color: 'bg-yellow-500' },
  { value: 'microsoft', label: 'Microsoft Outlook', color: 'bg-sky-500' },
  { value: 'other', label: 'Other', color: 'bg-slate-500' },
];

const PlatformBadge = ({ platform }) => {
  const platformInfo = PLATFORM_OPTIONS.find(p => p.value === platform) || PLATFORM_OPTIONS[6];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white ${platformInfo.color}`}>
      {platformInfo.label}
    </span>
  );
};

const SyncStatusBadge = ({ status }) => {
  if (status === 'success') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
        <CheckCircle2 className="h-3 w-3" />
        Synced
      </span>
    );
  } else if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-600">
        <XCircle className="h-3 w-3" />
        Error
      </span>
    );
  } else if (status === 'partial') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600">
        <Clock className="h-3 w-3" />
        Partial
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
      <Clock className="h-3 w-3" />
      Pending
    </span>
  );
};

export const CalendarSyncView = ({ selectedProperty, properties, onRefresh }) => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [feedToDelete, setFeedToDelete] = useState(null);
  const [exportUrl, setExportUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    property_id: selectedProperty?.id || '',
    feed_url: '',
    platform: 'airbnb',
    name: '',
  });

  const fetchFeeds = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getICalFeeds(selectedProperty?.id);
      setFeeds(response.data);
    } catch (error) {
      console.error('Error fetching feeds:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  const fetchExportUrl = useCallback(async () => {
    if (selectedProperty) {
      try {
        const response = await getPropertyICalUrl(selectedProperty.id);
        setExportUrl(response.data);
      } catch (error) {
        console.error('Error fetching export URL:', error);
      }
    }
  }, [selectedProperty]);

  useEffect(() => {
    fetchFeeds();
    fetchExportUrl();
  }, [fetchFeeds, fetchExportUrl]);

  useEffect(() => {
    if (selectedProperty) {
      setFormData(prev => ({ ...prev, property_id: selectedProperty.id }));
    }
  }, [selectedProperty]);

  const handleCreateFeed = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id || !formData.feed_url || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await createICalFeed(formData);
      toast.success('Calendar feed added successfully');
      setDialogOpen(false);
      setFormData({
        property_id: selectedProperty?.id || '',
        feed_url: '',
        platform: 'airbnb',
        name: '',
      });
      fetchFeeds();
    } catch (error) {
      toast.error('Failed to add calendar feed');
    }
  };

  const handleSync = async (feedId) => {
    setSyncing(prev => ({ ...prev, [feedId]: true }));
    try {
      const result = await syncICalFeed(feedId);
      const data = result.data;
      toast.success(
        `Sync complete: ${data.bookings_imported} imported, ${data.bookings_updated} updated, ${data.bookings_removed} removed`
      );
      fetchFeeds();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(prev => ({ ...prev, [feedId]: false }));
    }
  };

  const handleSyncAll = async () => {
    if (!selectedProperty) return;
    
    setSyncing(prev => ({ ...prev, all: true }));
    try {
      const result = await syncAllFeeds(selectedProperty.id);
      toast.success(`Synced ${result.data.feeds_synced} feeds`);
      fetchFeeds();
      onRefresh();
    } catch (error) {
      toast.error('Failed to sync all feeds');
    } finally {
      setSyncing(prev => ({ ...prev, all: false }));
    }
  };

  const handleDelete = async () => {
    if (!feedToDelete) return;
    
    try {
      await deleteICalFeed(feedToDelete.id);
      toast.success('Calendar feed removed');
      fetchFeeds();
    } catch (error) {
      toast.error('Failed to remove feed');
    } finally {
      setDeleteDialogOpen(false);
      setFeedToDelete(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const fullExportUrl = exportUrl ? 
    `${process.env.REACT_APP_BACKEND_URL}/api/properties/${selectedProperty?.id}/ical` : '';

  return (
    <div className="space-y-6" data-testid="calendar-sync-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Calendar Sync
          </h1>
          <p className="text-slate-500 mt-1">
            {selectedProperty ? selectedProperty.name : 'All Properties'} - Connect with Airbnb, VRBO, Google & more
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedProperty && feeds.length > 0 && (
            <Button
              variant="outline"
              onClick={handleSyncAll}
              disabled={syncing.all}
              data-testid="sync-all-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing.all ? 'animate-spin' : ''}`} />
              Sync All
            </Button>
          )}
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-feed-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Calendar Feed
          </Button>
        </div>
      </div>

      {/* Export Section */}
      {selectedProperty && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Export Your Calendar
            </CardTitle>
            <CardDescription>
              Share this URL with Airbnb, VRBO, Google Calendar, and other platforms to show your bookings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Input
                value={fullExportUrl}
                readOnly
                className="bg-white font-mono text-sm"
                data-testid="export-url-input"
              />
              <Button
                variant="outline"
                onClick={() => copyToClipboard(fullExportUrl)}
                className="shrink-0"
                data-testid="copy-url-btn"
              >
                {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button
                variant="outline"
                asChild
                className="shrink-0"
              >
                <a href={fullExportUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </div>
            
            {exportUrl?.instructions && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-slate-600">
                <div className="p-2 bg-white rounded border">
                  <strong className="text-red-500">Airbnb:</strong> Calendar → Import
                </div>
                <div className="p-2 bg-white rounded border">
                  <strong className="text-blue-600">VRBO:</strong> Calendar → Import/Export
                </div>
                <div className="p-2 bg-white rounded border">
                  <strong className="text-yellow-600">Google:</strong> Add Calendar → From URL
                </div>
                <div className="p-2 bg-white rounded border">
                  <strong className="text-sky-500">Outlook:</strong> Add → Subscribe from web
                </div>
                <div className="p-2 bg-white rounded border">
                  <strong className="text-emerald-500">Evolve:</strong> Contact support
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Feeds Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Link className="h-5 w-5 text-slate-600" />
            Import From External Calendars
          </CardTitle>
          <CardDescription>
            Add iCal feed URLs from Airbnb, VRBO, and other platforms to import their bookings
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : feeds.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Link className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p>No calendar feeds configured yet</p>
              <p className="text-sm mt-1">Add feeds from Airbnb, VRBO, or other platforms to import bookings</p>
            </div>
          ) : (
            <div className="space-y-3">
              {feeds.map((feed) => (
                <div
                  key={feed.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-100"
                  data-testid={`feed-${feed.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900">{feed.name}</span>
                      <PlatformBadge platform={feed.platform} />
                      <SyncStatusBadge status={feed.sync_status} />
                    </div>
                    <p className="text-xs text-slate-500 truncate">{feed.feed_url}</p>
                    {feed.last_sync && (
                      <p className="text-xs text-slate-400 mt-1">
                        Last synced: {format(new Date(feed.last_sync), 'MMM d, yyyy h:mm a')}
                      </p>
                    )}
                    {feed.sync_error && (
                      <p className="text-xs text-red-500 mt-1">{feed.sync_error}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSync(feed.id)}
                      disabled={syncing[feed.id]}
                      data-testid={`sync-feed-${feed.id}`}
                    >
                      <RefreshCw className={`h-4 w-4 ${syncing[feed.id] ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFeedToDelete(feed);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-feed-${feed.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Platform Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">How to Get iCal URLs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <span className="w-3 h-3 rounded bg-red-500"></span>
                Airbnb
              </h4>
              <ol className="list-decimal list-inside text-slate-600 space-y-1 pl-5">
                <li>Go to Calendar → Availability</li>
                <li>Click "Export Calendar"</li>
                <li>Copy the iCal link</li>
              </ol>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <span className="w-3 h-3 rounded bg-blue-600"></span>
                VRBO / HomeAway
              </h4>
              <ol className="list-decimal list-inside text-slate-600 space-y-1 pl-5">
                <li>Go to Calendar → Import/Export</li>
                <li>Click "Export"</li>
                <li>Copy the calendar URL</li>
              </ol>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <span className="w-3 h-3 rounded bg-emerald-500"></span>
                Evolve
              </h4>
              <ol className="list-decimal list-inside text-slate-600 space-y-1 pl-5">
                <li>Contact your Evolve rep</li>
                <li>Request your iCal feed URL</li>
                <li>They'll provide the link</li>
              </ol>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                <span className="w-3 h-3 rounded bg-yellow-500"></span>
                Google Calendar
              </h4>
              <ol className="list-decimal list-inside text-slate-600 space-y-1 pl-5">
                <li>Go to Calendar Settings</li>
                <li>Select your calendar</li>
                <li>Copy "Secret address in iCal format"</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Feed Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="add-feed-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">Add Calendar Feed</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateFeed} className="space-y-4">
            <div className="space-y-2">
              <Label>Property *</Label>
              <Select
                value={formData.property_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, property_id: value }))}
              >
                <SelectTrigger data-testid="feed-property-select">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id}>
                      {prop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Platform *</Label>
              <Select
                value={formData.platform}
                onValueChange={(value) => setFormData(prev => ({ ...prev, platform: value }))}
              >
                <SelectTrigger data-testid="feed-platform-select">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORM_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded ${opt.color}`}></span>
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Feed Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Airbnb - Beach House"
                className="bg-slate-50"
                data-testid="feed-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feed_url">iCal Feed URL *</Label>
              <Input
                id="feed_url"
                value={formData.feed_url}
                onChange={(e) => setFormData(prev => ({ ...prev, feed_url: e.target.value }))}
                placeholder="https://www.airbnb.com/calendar/ical/..."
                className="bg-slate-50 font-mono text-sm"
                data-testid="feed-url-input"
              />
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="feed-submit-btn"
              >
                Add Feed
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Calendar Feed</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{feedToDelete?.name}"? 
              Previously imported bookings will remain in the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CalendarSyncView;
